import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, qrUrl } from "../../lib/api";
import { inr, fmtDate, errMsg } from "../../lib/format";
import { StatusBadge } from "../../components/StatusBadge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../../components/ui/alert-dialog";
import { ArrowLeft, Upload, QrCode, Trash2 } from "lucide-react";

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [user, setUser] = useState(null);
  const [txns, setTxns] = useState([]);
  const [form, setForm] = useState({});
  const [adjust, setAdjust] = useState({ amount: "", reason: "" });
  const [pw, setPw] = useState("");

  const load = () => {
    api.get(`/admin/users/${id}`).then((r) => {
      setUser(r.data.user);
      setTxns(r.data.transactions);
      setForm(r.data.user);
    });
  };
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    try {
      await api.patch(`/admin/users/${id}`, {
        name: form.name, phone: form.phone, upi_id: form.upi_id, status: form.status,
        min_deposit: Number(form.min_deposit) || 0, max_deposit: Number(form.max_deposit) || 0,
        min_withdrawal: Number(form.min_withdrawal) || 0, max_withdrawal: Number(form.max_withdrawal) || 0,
      });
      toast.success("User updated");
      load();
    } catch (err) { toast.error(errMsg(err.response?.data?.detail)); }
  };

  const uploadQr = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/users/${id}/qr`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("QR uploaded");
      load();
    } catch (err) { toast.error(errMsg(err.response?.data?.detail)); }
  };

  const doAdjust = async () => {
    try {
      await api.post(`/admin/users/${id}/adjust`, { amount: Number(adjust.amount), reason: adjust.reason });
      toast.success("Balance adjusted");
      setAdjust({ amount: "", reason: "" });
      load();
    } catch (err) { toast.error(errMsg(err.response?.data?.detail)); }
  };

  const resetPw = async () => {
    try {
      await api.post(`/admin/users/${id}/password`, { new_password: pw });
      toast.success("Password reset");
      setPw("");
    } catch (err) { toast.error(errMsg(err.response?.data?.detail)); }
  };

  const deleteUser = async () => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      navigate("/admin/users");
    } catch (err) { toast.error(errMsg(err.response?.data?.detail)); }
  };

  const toggleSuspend = async () => {
    const next = user.status === "active" ? "suspended" : "active";
    await api.patch(`/admin/users/${id}`, { status: next });
    toast.success(next === "suspended" ? "User suspended" : "User reactivated");
    load();
  };

  if (!user) return null;

  return (
    <div>
      <button onClick={() => navigate("/admin/users")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft size={16} /> Back to Users</button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <p className="overline">User Profile</p>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1">{user.name}</h1>
          <p className="font-mono-num text-xs text-muted-foreground mt-1">{user.email}</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="suspend-user-btn" variant="outline" onClick={toggleSuspend} className="rounded-sm">
            {user.status === "active" ? "Suspend" : "Reactivate"}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="delete-user-btn" variant="destructive" className="rounded-sm gap-2"><Trash2 size={15} /> Delete</Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-sm">
              <AlertDialogHeader><AlertDialogTitle>Delete this user?</AlertDialogTitle><AlertDialogDescription>This permanently removes the user and all their transactions.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-user" onClick={deleteUser} className="rounded-sm bg-[#DC2626]">Delete</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-[#059669] text-white p-5 rounded-sm"><p className="overline text-white/70">Balance</p><p className="text-2xl font-bold font-mono-num mt-1" data-testid="detail-balance">{inr(user.balance)}</p></div>
        <div className="bg-card border border-border p-5 rounded-sm"><p className="overline">On Hold</p><p className="text-2xl font-bold font-mono-num mt-1">{inr(user.held_balance)}</p></div>
        <div className="bg-card border border-border p-5 rounded-sm"><p className="overline">Status</p><p className="text-lg font-bold mt-2 capitalize">{user.status}</p></div>
        <div className="bg-card border border-border p-5 rounded-sm"><p className="overline">Phone</p><p className="text-sm font-mono-num mt-2">{user.phone || "—"}</p></div>
      </div>

      <Tabs defaultValue="settings">
        <TabsList className="rounded-sm">
          <TabsTrigger value="settings" className="rounded-sm" data-testid="tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="qr" className="rounded-sm" data-testid="tab-qr">Payment QR/UPI</TabsTrigger>
          <TabsTrigger value="balance" className="rounded-sm" data-testid="tab-balance">Balance &amp; Security</TabsTrigger>
          <TabsTrigger value="txns" className="rounded-sm" data-testid="tab-txns">Transactions</TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="mt-6">
          <div className="bg-card border border-border rounded-sm p-6 max-w-2xl">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="overline">Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm h-11" /></div>
              <div><Label className="overline">Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 rounded-sm h-11" /></div>
              <div><Label className="overline">Min Deposit (₹)</Label><Input data-testid="min-deposit" type="number" value={form.min_deposit || 0} onChange={(e) => setForm({ ...form, min_deposit: e.target.value })} className="mt-2 rounded-sm h-11 font-mono-num" /></div>
              <div><Label className="overline">Max Deposit (₹)</Label><Input data-testid="max-deposit" type="number" value={form.max_deposit || 0} onChange={(e) => setForm({ ...form, max_deposit: e.target.value })} className="mt-2 rounded-sm h-11 font-mono-num" /></div>
              <div><Label className="overline">Min Withdrawal (₹)</Label><Input data-testid="min-withdrawal" type="number" value={form.min_withdrawal || 0} onChange={(e) => setForm({ ...form, min_withdrawal: e.target.value })} className="mt-2 rounded-sm h-11 font-mono-num" /></div>
              <div><Label className="overline">Max Withdrawal (₹)</Label><Input data-testid="max-withdrawal" type="number" value={form.max_withdrawal || 0} onChange={(e) => setForm({ ...form, max_withdrawal: e.target.value })} className="mt-2 rounded-sm h-11 font-mono-num" /></div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">Set 0 to disable a limit.</p>
            <Button data-testid="save-user-btn" onClick={save} className="rounded-sm mt-5 h-11">Save Changes</Button>
          </div>
        </TabsContent>

        <TabsContent value="qr" className="mt-6">
          <div className="bg-card border border-border rounded-sm p-6 max-w-2xl">
            <h3 className="font-heading font-bold text-lg mb-4">Payment QR / UPI (visible only to this user)</h3>
            <div className="flex flex-col sm:flex-row gap-6">
              {user.qr_storage_path ? (
                <img src={qrUrl(user.qr_storage_path)} alt="QR" data-testid="admin-user-qr" className="w-44 h-44 object-contain border border-border rounded-sm bg-white p-2" />
              ) : (
                <div className="w-44 h-44 flex flex-col items-center justify-center border border-dashed border-border rounded-sm grid-bg text-muted-foreground"><QrCode size={30} /><span className="text-xs mt-2">No QR</span></div>
              )}
              <div className="flex-1 space-y-4">
                <div>
                  <Label className="overline">UPI ID</Label>
                  <Input data-testid="qr-upi-input" value={form.upi_id || ""} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} className="mt-2 rounded-sm h-11 font-mono-num" placeholder="name@bank" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={save} className="rounded-sm h-11">Save UPI</Button>
                  <input ref={fileRef} type="file" accept="image/*" onChange={uploadQr} className="hidden" data-testid="qr-file-input" />
                  <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-sm h-11 gap-2"><Upload size={15} /> Upload QR</Button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="balance" className="mt-6">
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
            <div className="bg-card border border-border rounded-sm p-6">
              <h3 className="font-heading font-bold text-lg mb-4">Manual Balance Adjustment</h3>
              <div className="space-y-4">
                <div><Label className="overline">Amount (₹, use negative to deduct)</Label><Input data-testid="adjust-amount" type="number" value={adjust.amount} onChange={(e) => setAdjust({ ...adjust, amount: e.target.value })} className="mt-2 rounded-sm h-11 font-mono-num" /></div>
                <div><Label className="overline">Reason</Label><Input data-testid="adjust-reason" value={adjust.reason} onChange={(e) => setAdjust({ ...adjust, reason: e.target.value })} className="mt-2 rounded-sm h-11" placeholder="Correction / bonus" /></div>
                <Button data-testid="adjust-submit" onClick={doAdjust} className="rounded-sm h-11 w-full">Apply Adjustment</Button>
              </div>
            </div>
            <div className="bg-card border border-border rounded-sm p-6">
              <h3 className="font-heading font-bold text-lg mb-4">Reset Password</h3>
              <div className="space-y-4">
                <div><Label className="overline">New Password</Label><Input data-testid="reset-pw-input" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-2 rounded-sm h-11" /></div>
                <Button data-testid="reset-pw-submit" onClick={resetPw} className="rounded-sm h-11 w-full">Reset Password</Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="txns" className="mt-6">
          <div className="border border-border rounded-sm overflow-x-auto bg-card">
            <table className="w-full text-sm">
              <thead><tr className="text-left overline border-b border-border">
                <th className="px-5 py-3 font-semibold">Type</th><th className="px-5 py-3 font-semibold">Amount</th>
                <th className="px-5 py-3 font-semibold">UTR</th><th className="px-5 py-3 font-semibold hidden md:table-cell">Date</th><th className="px-5 py-3 font-semibold">Status</th>
              </tr></thead>
              <tbody>
                {txns.length === 0 ? (<tr><td colSpan="5" className="px-5 py-6 text-muted-foreground">No transactions.</td></tr>) : txns.map((t) => (
                  <tr key={t.id} className="border-b border-border/40 last:border-0">
                    <td className="px-5 py-3 capitalize">{t.type}</td>
                    <td className="px-5 py-3 font-mono-num font-medium">{inr(t.amount)}</td>
                    <td className="px-5 py-3 font-mono-num text-xs">{t.utr || "—"}</td>
                    <td className="px-5 py-3 text-xs text-muted-foreground hidden md:table-cell">{fmtDate(t.created_at)}</td>
                    <td className="px-5 py-3"><StatusBadge status={t.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
